import * as turf from "@turf/turf";

// Increased from 20m to 50m to better tolerate typical GPS inaccuracy and update latency,
// so users are not falsely considered outside a waypoint when they are effectively there.
export const WAYPOINT_PROXIMITY_THRESHOLD_METERS = 50;

export const checkApproachingWaypoint = (
    userLocationCoordinates: number[],
    waypointLocationCoordinates: number[]
): {
    isWithinProximity: boolean;
    distanceMeters: number;
} => {
    const from = turf.point(userLocationCoordinates);
    const to = turf.point(waypointLocationCoordinates);

    const options = { units: "meters" as turf.Units };
    const distance = turf.distance(from, to, options);

    return {
        isWithinProximity: distance <= WAYPOINT_PROXIMITY_THRESHOLD_METERS,
        distanceMeters: distance,
    };
};

export const checkDepartingWaypoint = (
    userLocationCoordinates: number[],
    waypointLocationCoordinates: number[]
): boolean => {
    const distance = turf.distance(userLocationCoordinates, waypointLocationCoordinates, { units: "meters" });
    return distance > WAYPOINT_PROXIMITY_THRESHOLD_METERS;
};

export const convertDistanceToReadable = (distanceMeters: number): string => {
    const unitOfMeasure = distanceMeters < 1609.34 / 4 ? "feet" : "miles"; // Less than 1/4 mile
    if (unitOfMeasure === "feet") {
        const feet = distanceMeters * 3.28084;
        return `${Math.round(feet)} ft`;
    } else {
        const miles = distanceMeters * 0.000621371;
        return `${miles.toFixed(1)} mi`;
    }
};

// Straight-line legs between waypoints, so this underestimates winding routes - good enough for a glanceable total.
export const remainingRouteDistanceMeters = (
    waypointCoordinates: number[][],
    startCoordinates: number[] | null
): number => {
    const path = startCoordinates ? [startCoordinates, ...waypointCoordinates] : waypointCoordinates;
    return path.slice(1).reduce((total, coordinates, index) => {
        return total + turf.distance(path[index], coordinates, { units: "meters" });
    }, 0);
};
